/* eslint-disable no-nested-ternary */

import {
	Form,
	Input,
	Button,
	message,
	Checkbox,
	Radio,
	Select,
	Upload,
	Divider,
	Typography,
	Space,
	Card,
	Row,
	Col,
	InputNumber,
	Dropdown,
	Menu,
	Tabs,
	Switch,
	Collapse,
} from 'antd'
import countryCodeData from 'data/countryCodeData'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import editClient, { EditClientBody } from 'api/clients/editClient'
// eslint-disable-next-line import/no-extraneous-dependencies
import { ValidateErrorEntity } from 'rc-field-form/lib/interface'
// eslint-disable-next-line import/no-extraneous-dependencies
import { DefaultOptionType } from 'rc-select/lib/Select.d'
import React, { useEffect, useState } from 'react'
import { getClientsAction } from 'slices/client'
import {
	allBillingCycle,
	AllowedModule,
	allowedModuleTypes,
	BillingCycle,
	ClientInvoiceDetails,
	ClientProfile,
	ClientTaxation,
	Currency,
	CurrentLimit,
	EdgePlatform,
	edgePlatforms,
	PlatformStack,
	platformStacks,
	supportedCurrencies,
	PlanDetails,
} from 'types'
import handleFormError from 'utils/handleFormError'
import {
	LoadingOutlined,
	MinusCircleOutlined,
	PlusOutlined,
	PhoneOutlined,
	RobotOutlined,
} from '@ant-design/icons'
import upload, { FileUpload } from 'api/file/upload'
import { UploadRequestOption } from 'rc-upload/lib/interface'
import handleAxiosError from 'utils/handleAxiosError'
import ImgCrop from 'antd-img-crop'
import toTileCase from 'utils/toTitleCase'
import getAdmins from 'api/admin/getAdmins'
import isValidEmail from 'utils/isValidEmail'
import getPlatformLabel from 'utils/getPlatformLabel'
import {
	normalizeClientChargeForSave,
	toChargeFormValue,
} from 'utils/normalizeClientCharge'
import AllowedModulesTable from 'component/Client/AllowedModulesTable'

import getClientPlanData from 'api/clients/getClientPlanData'
// eslint-disable-next-line import/no-extraneous-dependencies
import { MenuClickEventHandler } from 'rc-menu/lib/interface'

const { Item, List } = Form
const { Option } = Select
const { Title } = Typography
const { TabPane } = Tabs
const { Panel } = Collapse

const validAllowedModuleTypeSet = new Set<string>(
	allowedModuleTypes as readonly string[]
)

const sanitizeAllowedModules = (modules: AllowedModule[] = []) =>
	modules.filter(module => validAllowedModuleTypeSet.has(module.type))

interface PropTypes {
	closeDrawer: Function
	singleClient: ClientProfile
}

interface FormBodyTypes {
	logo: string | null
	address: string
	billingCycle: 'MONTHLY' | 'YEARLY'
	city: string
	country: string
	country_code: string
	email: string
	title: string
	limit: CurrentLimit
	mobile: string
	organizationName: string
	billingCycleAmount: string
	state: string
	storageAllowed: string
	website: string
	whatcxCharge: string
	whatcxChargeInbound: number
	whatcxChargeOutbound: number
	currency_selected: Currency
	taxation: ClientTaxation[]
	invoiceDetails: ClientInvoiceDetails[]
	csmUser: string[]
	notificationEmails: string[]
	invoiceEmails: string[]
	shouldGenerateAutoInvoice: boolean
	allowedModules: AllowedModule[]
	platform: EdgePlatform
	platformStack: PlatformStack
	tdsEnabled: boolean
	broadcastLimit: number
	voiceCalling?: {
		enabled: boolean
		pricing?: {
			currency?: string // Optional - uses client's default currency if not provided
			virtualNumberSetupFee: number
			virtualNumberMonthlyRental: number
			perChannelMonthlyRental: number
			numberOfChannels: number
			inboundPerMinuteRate: number
			outboundPerMinuteRate: number
		}
	}
	aiServices?: {
		enabled: boolean
		pricing?: {
			currency: string
			perTokenRate: number
			perTranscriptionMinuteRate?: number
			perDocumentIntelligenceUnitRate?: number
		}
	}
	enablexVoice?: {
		appId?: string
		appKey?: string
		fromNumber?: string
		webhookUrl?: string
		isConfigured?: boolean
	}
	aiPlatformConfig?: {
		credentialMode?: 'shared' | 'byok'
		openai?: {
			apiKey?: string
			defaultModel?: string
		}
	}
}

const DEFAULT_AI_SERVICES_PRICING = {
	currency: 'INR' as const,
	perTokenRate: 0.002,
	perTranscriptionMinuteRate: 0,
	perDocumentIntelligenceUnitRate: 0,
}

const buildAiServicesPayload = (
	formAi: FormBodyTypes['aiServices'] | undefined,
	existing: ClientProfile['activePlan']['aiServices'] | undefined
): EditClientBody['plan']['aiServices'] => {
	const enabled = formAi?.enabled ?? existing?.enabled ?? false
	if (!enabled) {
		return {
			enabled: false,
			...(existing?.pricing || formAi?.pricing
				? {
						pricing: {
							currency:
								formAi?.pricing?.currency ??
								existing?.pricing?.currency ??
								DEFAULT_AI_SERVICES_PRICING.currency,
							perTokenRate:
								formAi?.pricing?.perTokenRate ??
								existing?.pricing?.perTokenRate ??
								DEFAULT_AI_SERVICES_PRICING.perTokenRate,
							perTranscriptionMinuteRate:
								formAi?.pricing?.perTranscriptionMinuteRate ??
								existing?.pricing?.perTranscriptionMinuteRate ??
								DEFAULT_AI_SERVICES_PRICING.perTranscriptionMinuteRate,
							perDocumentIntelligenceUnitRate:
								formAi?.pricing?.perDocumentIntelligenceUnitRate ??
								existing?.pricing?.perDocumentIntelligenceUnitRate ??
								DEFAULT_AI_SERVICES_PRICING.perDocumentIntelligenceUnitRate,
						},
				  }
				: {}),
		}
	}

	return {
		enabled: true,
		pricing: {
			currency:
				formAi?.pricing?.currency ??
				existing?.pricing?.currency ??
				DEFAULT_AI_SERVICES_PRICING.currency,
			perTokenRate: Number(
				formAi?.pricing?.perTokenRate ??
					existing?.pricing?.perTokenRate ??
					DEFAULT_AI_SERVICES_PRICING.perTokenRate
			),
			perTranscriptionMinuteRate: Number(
				formAi?.pricing?.perTranscriptionMinuteRate ??
					existing?.pricing?.perTranscriptionMinuteRate ??
					DEFAULT_AI_SERVICES_PRICING.perTranscriptionMinuteRate
			),
			perDocumentIntelligenceUnitRate: Number(
				formAi?.pricing?.perDocumentIntelligenceUnitRate ??
					existing?.pricing?.perDocumentIntelligenceUnitRate ??
					DEFAULT_AI_SERVICES_PRICING.perDocumentIntelligenceUnitRate
			),
		},
	}
}

const buildAiPlatformConfigPayload = (
	formConfig: FormBodyTypes['aiPlatformConfig'] | undefined,
	existing: ClientProfile['aiPlatformConfig'] | undefined
): EditClientBody['aiPlatformConfig'] => ({
	credentialMode:
		formConfig?.credentialMode ?? existing?.credentialMode ?? 'shared',
	openai: {
		defaultModel: (
			formConfig?.openai?.defaultModel ??
			existing?.openai?.defaultModel ??
			'gpt-4o-mini'
		).trim(),
		...(formConfig?.openai?.apiKey?.trim()
			? { apiKey: formConfig.openai.apiKey.trim() }
			: {}),
	},
})

const getEditClientInitialValues = (
	singleClient: ClientProfile
): FormBodyTypes => ({
	logo: singleClient.logo,
	limit: singleClient.currentLimit,
	address: singleClient.address,
	city: singleClient.city,
	country: singleClient.country,
	country_code: singleClient.mobileNumber.country,
	email: singleClient.email,
	title: singleClient.activePlan.title,
	billingCycle: singleClient.activePlan.billingCycle,
	mobile: singleClient.mobileNumber.mobile,
	organizationName: singleClient.organizationName,
	state: singleClient.state,
	storageAllowed: String(
		singleClient.activePlan.storageAllowed / (1024 * 1024 * 1024)
	),
	website: singleClient.website,
	billingCycleAmount: !singleClient.activePlan.perMonth
		? String(singleClient.activePlan.perYear)
		: String(singleClient.activePlan.perMonth),
	whatcxCharge: String(toChargeFormValue(singleClient.whatcxCharge)),
	whatcxChargeInbound: toChargeFormValue(singleClient.whatcxChargeInbound),
	whatcxChargeOutbound: toChargeFormValue(singleClient.whatcxChargeOutbound),
	currency_selected: singleClient.currency_selected || 'INR',
	taxation: singleClient.taxation || [],
	invoiceDetails: singleClient.invoiceDetails || [],
	csmUser:
		singleClient?.relationShipManager?.map(m =>
			typeof m === 'string' ? m : m._id
		) || [],
	notificationEmails: singleClient?.notificationEmails
		? singleClient.notificationEmails.split(',')
		: [],
	invoiceEmails: singleClient?.invoiceEmails
		? singleClient.invoiceEmails.split(',')
		: [],
	shouldGenerateAutoInvoice: singleClient.shouldGenerateAutoInvoice ?? true,
	allowedModules: sanitizeAllowedModules(
		singleClient.activePlan.allowedModules || []
	),
	platform: singleClient.selectedEDGEPlatform ?? 'DASH_CX',
	platformStack: singleClient.platformStack || 'V1',
	tdsEnabled: singleClient.tdsEnabled ?? false,
	broadcastLimit: singleClient.broadcastLimit ?? 0,
	voiceCalling: singleClient.activePlan?.voiceCalling || undefined,
	aiServices: singleClient.activePlan?.aiServices
		? { ...singleClient.activePlan.aiServices }
		: { enabled: false },
	enablexVoice: singleClient.enablexVoice || undefined,
	aiPlatformConfig: {
		credentialMode: singleClient.aiPlatformConfig?.credentialMode || 'shared',
		openai: {
			defaultModel:
				singleClient.aiPlatformConfig?.openai?.defaultModel || 'gpt-4o-mini',
			apiKey: '',
		},
	},
})

/**
 * Form Component for Editing Single Client in Client table
 *
 * @param closeDrawer - function which get triggered when edit Client drawer get close
 * @returns updated data of the Client
 */
const EditClientForm: React.FC<PropTypes> = ({ closeDrawer, singleClient }) => {
	const dispatch = useAppDispatch()

	const { currentPage } = useAppSelector(state => state.client)

	const [loading, setLoading] = useState<boolean>(false)
	const [uploadError, setUploadError] = useState(false)
	const [csmUsersOptions, setCsmUsersOptions] = useState<DefaultOptionType[]>([])
	const [plans, setPlans] = useState<PlanDetails[]>([])

	const [clientBillingCycle, setClientBillingCycle] = useState<BillingCycle>(
		singleClient.activePlan.perMonth !== 0 ? 'MONTHLY' : 'YEARLY'
	)

	const [form] = Form.useForm<FormBodyTypes>()

	const allowedModules: AllowedModule[] | undefined = Form.useWatch(
		'allowedModules',
		form
	)

	const prefixSelector = (
		<Item name='country_code' noStyle>
			<Select
				dropdownClassName='noMinWidth'
				showSearch
				optionFilterProp='children'
				placeholder='+91'
			>
				{countryCodeData.map(option => (
					<Select.Option key={option.id} value={option.code}>
						{`+${option.phone}`}
					</Select.Option>
				))}
			</Select>
		</Item>
	)

	/**
	 * Upload file function
	 */
	const uploadFile = async (options: UploadRequestOption<FileUpload>) => {
		try {
			setLoading(true)
			const { url } = await upload(options)
			form.setFieldsValue({
				logo: url,
			})
			setLoading(false)
		} catch (error: any) {
			setLoading(false)
			handleAxiosError(error)
		}
	}

	const beforeUpload = (file: File) => {
		const isImage = file.type === 'image/png' || file.type === 'image/jpeg'
		if (!isImage) {
			message.error(`Only Image file accepted`)
			setUploadError(true)
		} else if (file.size > 1024 * 1024 * 1) {
			message.error('Image must be less than 1MB')
			setUploadError(true)
			return false
		}
		setUploadError(false)
		return isImage || Upload.LIST_IGNORE
	}

	const onFinish = async (values: FormBodyTypes) => {
		// Merge stored field values so saves from either tab include Webhook Stack + plan modules.
		const mergedValues = {
			...form.getFieldsValue(true),
			...values,
		} as FormBodyTypes
		const originalAllowedModules =
			mergedValues.allowedModules ??
			sanitizeAllowedModules(singleClient.activePlan?.allowedModules ?? [])
		const sanitizedAllowedModules = sanitizeAllowedModules(originalAllowedModules)
		if (sanitizedAllowedModules.length !== originalAllowedModules.length) {
			message.warning(
				'Unsupported v2 modules were skipped while saving this client on v1 admin.'
			)
		}

		const body: EditClientBody = {
			plan: {
				title: mergedValues.title,
				storageAllowed:
					parseInt(mergedValues.storageAllowed, 10) * (1024 * 1024 * 1024),
				billingCycle: mergedValues.billingCycle,
				perMonth:
					mergedValues.billingCycle === 'MONTHLY'
						? parseInt(mergedValues.billingCycleAmount, 10)
						: 0,
				perYear:
					mergedValues.billingCycle === 'YEARLY'
						? parseInt(mergedValues.billingCycleAmount, 10)
						: 0,
				// modules: values.allowedModules,
				modules: sanitizedAllowedModules.map(m =>
					m.type === 'EDGE_CX_AI' || m.type === 'EXTERNAL_BOT'
						? { ...m, count: 0 }
						: m
				),
				voiceCalling: mergedValues.voiceCalling,
				aiServices: buildAiServicesPayload(
					mergedValues.aiServices,
					singleClient.activePlan?.aiServices
				),
			},
			logo: mergedValues.logo || undefined,
			limit: mergedValues.limit,
			organizationName: mergedValues.organizationName,
			email: mergedValues.email,
			address: mergedValues.address || '',
			city: mergedValues.city || '',
			state: mergedValues.state || '',
			website: mergedValues.website || undefined,
			country: mergedValues.country,
			mobileNumber: {
				mobile: mergedValues.mobile,
				country:
					mergedValues.country_code === undefined ? 'IN' : mergedValues.country_code,
			},
			whatcxCharge: normalizeClientChargeForSave(mergedValues.whatcxCharge),
			whatcxChargeInbound: normalizeClientChargeForSave(
				mergedValues.whatcxChargeInbound
			),
			whatcxChargeOutbound: normalizeClientChargeForSave(
				mergedValues.whatcxChargeOutbound
			),
			currency_selected: mergedValues.currency_selected,
			taxation: mergedValues.taxation,
			csmUser: mergedValues.csmUser,
			notificationEmails: (mergedValues.notificationEmails ?? []).join(','),
			invoiceEmails: (mergedValues.invoiceEmails ?? []).join(','),
			invoiceDetails: mergedValues.invoiceDetails,
			shouldGenerateAutoInvoice: mergedValues.shouldGenerateAutoInvoice,
			platform: mergedValues.platform,
			platformStack:
				mergedValues.platformStack ?? singleClient.platformStack ?? 'V1',
			tdsEnabled: mergedValues.tdsEnabled,
			broadcastLimit: Number(mergedValues.broadcastLimit),
			enablexVoice: mergedValues.enablexVoice
				? {
						appId: mergedValues.enablexVoice.appId || '',
						appKey: mergedValues.enablexVoice.appKey || '',
						fromNumber: mergedValues.enablexVoice.fromNumber || '',
						webhookUrl: mergedValues.enablexVoice.webhookUrl || '',
						isConfigured: !!(
							mergedValues.enablexVoice.appId &&
							mergedValues.enablexVoice.appKey &&
							mergedValues.enablexVoice.fromNumber
						),
				  }
				: undefined,
			aiPlatformConfig: buildAiPlatformConfigPayload(
				mergedValues.aiPlatformConfig,
				singleClient.aiPlatformConfig
			),
		}
		try {
			setLoading(true)
			await editClient(singleClient._id, body)
			setLoading(false)
			await closeDrawer()
			const options = {
				page: currentPage,
				size: 10,
				query: '',
			}
			dispatch(getClientsAction(options))
			message.success('Client Updated Successfully')
		} catch (error: any) {
			setLoading(false)
		}
	}
	const handlePlanSelect: MenuClickEventHandler = event => {
		const selectedPlan = plans.find(ele => ele.id === event.key)

		if (!selectedPlan) {
			message.error('Plan does not exist ')
			return
		}

		form.setFieldsValue({
			title: selectedPlan.title,
			allowedModules: selectedPlan.allowedModules,
			storageAllowed: String(selectedPlan.storageAllowed / (1024 * 1024 * 1024)),
			billingCycle: selectedPlan.billingCycle,
			billingCycleAmount:
				selectedPlan.billingCycle === 'MONTHLY'
					? String(selectedPlan.perMonth)
					: selectedPlan.billingCycle === 'YEARLY'
					? String(selectedPlan.perYear)
					: '0',
		})
		setClientBillingCycle(selectedPlan.billingCycle)
	}

	const onFinishFailed = (errorInfo: ValidateErrorEntity) => {
		handleFormError(errorInfo)
	}

	useEffect(() => {
		const loadCsmUserOptions = async () => {
			try {
				const admins = await getAdmins({ page: 1, size: 200 })

				const options = admins.records.map(admin => ({
					label: `${admin.firstName} ${admin.lastName} (${admin.email} - ${admin.mobile})`,
					value: admin._id,
				}))

				setCsmUsersOptions([...options])
			} catch (error: any) {
				handleAxiosError(error)
			}
		}

		loadCsmUserOptions()
		const fetchClientData = async () => {
			try {
				const moduleData = await getClientPlanData()
				setPlans(moduleData.plans)
			} catch (error) {
				setPlans([])
			}
		}
		fetchClientData()
	}, [])

	useEffect(() => {
		form.setFieldsValue(getEditClientInitialValues(singleClient))
		setClientBillingCycle(
			singleClient.activePlan.perMonth !== 0 ? 'MONTHLY' : 'YEARLY'
		)
	}, [form, singleClient._id, singleClient.updatedAt])

	return (
		<Form
			form={form}
			name='editClient'
			onFinish={onFinish}
			onFinishFailed={onFinishFailed}
			layout='vertical'
			initialValues={getEditClientInitialValues(singleClient)}
		>
			<Tabs defaultActiveKey='1' type='card' destroyInactiveTabPane={false}>
				<TabPane tab='Client Details' key='1'>
					<Item
						label='Organization Name'
						name='organizationName'
						rules={[{ required: true, message: 'Organization name is required' }]}
					>
						<Input />
					</Item>

					<Item
						label='Upload Logo'
						name='logo'
						extra='Recommended resolution is 640*640 with file size less than 1MB,
            keep visual elements centered'
					>
						<ImgCrop modalTitle='Crop Image'>
							<Upload
								name='logo'
								maxCount={1}
								beforeUpload={beforeUpload}
								showUploadList={!uploadError}
								multiple={false}
								customRequest={uploadFile}
								listType='picture-card'
								accept='image/*'
								onRemove={() =>
									form.setFieldsValue({
										logo: null,
									})
								}
							>
								<div>
									{loading ? <LoadingOutlined /> : <PlusOutlined />}
									<div style={{ marginTop: 8 }}>Upload</div>
								</div>
							</Upload>
						</ImgCrop>
					</Item>

					<Item
						label='Email'
						name='email'
						rules={[
							{
								required: true,
								message: 'Email is required',
							},

							{
								pattern:
									/^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
								message: 'Please enter a valid Email',
							},
						]}
					>
						<Input />
					</Item>

					<Item label='Address' name='address'>
						<Input />
					</Item>
					<Item label='City' name='city'>
						<Input />
					</Item>
					<Item label='State' name='state'>
						<Input />
					</Item>
					<Item label='Website' name='website'>
						<Input />
					</Item>
					<Item
						label='Country'
						name='country'
						rules={[{ required: true, message: 'Country is required' }]}
					>
						<Input />
					</Item>
					<Item label='Mobile Number' name='mobile' rules={[{ required: false }]}>
						<Input
							addonBefore={prefixSelector}
							style={{ width: '100%' }}
							placeholder='xxx-xxx-xxxx'
							type='tel'
						/>
					</Item>
					<Item
						label='Limit'
						name='limit'
						rules={[{ required: true, message: 'Limit is required' }]}
					>
						<Select placeholder='Select Limit'>
							<Option value='TIER_0K'>TIER 0K</Option>
							<Option value='TIER_1K'>TIER 1K</Option>
							<Option value='TIER_10K'>TIER 10K</Option>
							<Option value='TIER_100K'>TIER 100K</Option>
							<Option value='TIER_UNLIMITED'>TIER UNLIMITED</Option>
						</Select>
					</Item>

					<Divider />

					<Title level={4}>Customer Success</Title>

					<Item label='Customer Success Managers' name='csmUser'>
						<Select
							placeholder='Select Customer Success Managers'
							options={csmUsersOptions}
							optionFilterProp='label'
							mode='multiple'
							allowClear
							showSearch
							loading={csmUsersOptions.length === 0}
						/>
					</Item>

					<Item
						label='Notification Emails'
						name='notificationEmails'
						rules={[
							() => ({
								validator(_, emails: string[]) {
									for (let index = 0; index < emails.length; index += 1) {
										const email = emails[index]
										if (isValidEmail(email) === false) {
											return Promise.reject(new Error(`Invalid Email Provided: ${email}`))
										}
									}

									return Promise.resolve()
								},
							}),
						]}
					>
						<Select placeholder='Add all emails to notify' mode='tags' />
					</Item>

					<Divider />

					<Title level={4}>Billing</Title>

					<Item
						label='Currency'
						name='currency_selected'
						rules={[{ required: true, message: 'Currency is required' }]}
					>
						<Select
							placeholder='Select Currency'
							options={supportedCurrencies.map(c => ({
								label: c,
								value: c,
								disabled: !['INR', 'USD'].includes(c),
							}))}
						/>
					</Item>

					<Item
						label='WhatCX Charge'
						name='whatcxCharge'
						rules={[
							{ required: true, message: 'WhatCX Charge is required' },
							{
								validator: (_, value) => {
									const parsed = normalizeClientChargeForSave(value)
									if (parsed < -1) {
										return Promise.reject(
											new Error('Must be -1 (default) or a positive number')
										)
									}
									return Promise.resolve()
								},
							},
						]}
						tooltip='Use -1 to fall back to the pricing chart default'
					>
						<Input type='number' min={-1} step='0.0001' />
					</Item>

					<Item
						label='Inbound Conversation Charge'
						name='whatcxChargeInbound'
						rules={[
							{
								type: 'number',
								min: -1,
								message: 'Must be -1 (default) or a positive number',
							},
							{ required: false },
						]}
					>
						<InputNumber
							placeholder='Leave empty to use pricing chart default (-1)'
							min={-1}
							step={0.0001}
							precision={4}
							style={{ width: '100%' }}
						/>
					</Item>

					<Item
						label='Outbound Conversation Charge'
						name='whatcxChargeOutbound'
						rules={[
							{
								type: 'number',
								min: -1,
								message: 'Must be -1 (default) or a positive number',
							},
							{ required: false },
						]}
					>
						<InputNumber
							placeholder='Leave empty to use pricing chart default (-1)'
							min={-1}
							step={0.0001}
							precision={4}
							style={{ width: '100%' }}
						/>
					</Item>

					<Item
						label='Billing Emails'
						name='invoiceEmails'
						rules={[
							() => ({
								validator(_, emails: string[]) {
									for (let index = 0; index < emails.length; index += 1) {
										const email = emails[index]
										if (isValidEmail(email) === false) {
											return Promise.reject(new Error(`Invalid Email Provided: ${email}`))
										}
									}

									return Promise.resolve()
								},
							}),
						]}
					>
						<Select placeholder='Add all emails to notify' mode='tags' />
					</Item>

					<Item name='tdsEnabled' valuePropName='checked'>
						<Checkbox>Will deduct TDS</Checkbox>
					</Item>

					<Item
						label='Webhook Stack'
						name='platformStack'
						tooltip='V2 clients are accepted on v1 then forwarded to the v2 webhook server for processing.'
						rules={[{ required: true, message: 'Webhook stack is required' }]}
					>
						<Select
							options={platformStacks.map(stack => ({
								label: stack === 'V1' ? 'V1 (production)' : 'V2 (forward to v2 server)',
								value: stack,
							}))}
						/>
					</Item>

					<Item name='shouldGenerateAutoInvoice' valuePropName='checked'>
						<Checkbox>Should Auto Generate Wallet Invoice</Checkbox>
					</Item>

					<Divider />

					<List name='invoiceDetails'>
						{(fields, { add, remove }) => (
							<>
								<Title level={4}>Invoice Details</Title>
								{fields.map(field => (
									<div key={field.key}>
										<Space>
											<Item
												label='Label'
												name={[field.name, 'key']}
												rules={[{ required: true, message: 'Invoice label is required' }]}
											>
												<Input />
											</Item>
											<Item
												label='Value'
												name={[field.name, 'value']}
												rules={[{ required: true, message: 'Invoice value is required' }]}
											>
												<Input />
											</Item>
											<Button
												htmlType='button'
												type='text'
												danger
												icon={<MinusCircleOutlined />}
												onClick={() => remove(field.name)}
											/>
										</Space>
									</div>
								))}
								<Button
									htmlType='button'
									icon={<PlusOutlined />}
									onClick={() =>
										add({
											key: '',
											value: '',
										})
									}
								>
									Add Details
								</Button>
							</>
						)}
					</List>

					<Divider />

					<List name='taxation'>
						{(fields, { add, remove }) => (
							<>
								<Title level={4}>Taxation</Title>
								{fields.map(field => (
									<div key={field.key}>
										<Space>
											<Item
												label='Label'
												name={[field.name, 'key']}
												rules={[{ required: true, message: 'Tax label is required' }]}
											>
												<Input />
											</Item>
											<Item
												label='Precentage'
												name={[field.name, 'percentage']}
												rules={[{ required: true, message: 'Tax percentage is required' }]}
											>
												<Input type='number' min={0} max={100} step='0.0001' />
											</Item>
											<Button
												htmlType='button'
												type='text'
												danger
												icon={<MinusCircleOutlined />}
												onClick={() => remove(field.name)}
											/>
										</Space>
									</div>
								))}
								<Button
									htmlType='button'
									icon={<PlusOutlined />}
									onClick={() =>
										add({
											key: '',
											percentage: '',
										})
									}
								>
									Add Tax
								</Button>
							</>
						)}
					</List>
				</TabPane>

				<TabPane tab='Plan Configuration' key='2'>
					<Row justify='space-between'>
						<Col>
							<Title level={4}>Plan Details</Title>
						</Col>

						<Col>
							<Dropdown
								overlay={
									<Menu onClick={handlePlanSelect}>
										{plans.map(ele => (
											<Menu.Item key={ele.id}>{ele.title}</Menu.Item>
										))}
									</Menu>
								}
							>
								<Button>
									<Space>Select Plan</Space>
								</Button>
							</Dropdown>
						</Col>
					</Row>

					<Item
						label='Title'
						name='title'
						rules={[{ required: true, message: 'Title is required' }]}
					>
						<Input />
					</Item>

					<Item
						label='Platform'
						name='platform'
						rules={[{ required: true, message: 'Platform is Required!' }]}
					>
						<Select
							showSearch
							optionFilterProp='label'
							options={edgePlatforms.map(platform => ({
								label: getPlatformLabel(platform),
								value: platform,
							}))}
						/>
					</Item>

					<Item
						label='Storage Allowed'
						name='storageAllowed'
						rules={[{ required: true, message: 'Storage allowed data is required' }]}
					>
						<Input type='number' suffix='GB' min={0} />
					</Item>
					<Item
						label='Broadcast Limit'
						rules={[
							{
								required: true,
								message: 'Broadcast Limit is required',
							},
						]}
						name='broadcastLimit'
					>
						<Input type='number' min={0} />
					</Item>
					<Item
						label='Billing Cycle'
						name='billingCycle'
						rules={[{ required: true, message: 'Billing cycle data is required' }]}
					>
						<Radio.Group
							onChange={e => setClientBillingCycle(e.target.value)}
							value={clientBillingCycle}
							options={allBillingCycle.map(b => ({
								value: b,
								label: toTileCase(b),
							}))}
						/>
					</Item>
					{clientBillingCycle !== null && (
						<Item
							label={
								clientBillingCycle === 'MONTHLY'
									? 'Per Month'
									: // TODO: Enable before invoice revamp
									// : clientBillingCycle === 'QUARTERLY'
									// ? 'Per Quarter'
									// : clientBillingCycle === 'HALF_YEARLY'
									// ? 'Per Half Year'
									clientBillingCycle === 'YEARLY'
									? 'Per Year'
									: 'Unkown'
							}
							name='billingCycleAmount'
							rules={[
								{ required: true, message: 'Billing cycle amount data is required' },
							]}
						>
							<Input type='number' min={0} />
						</Item>
					)}

					<h4>Modules</h4>
					<AllowedModulesTable allowedModules={allowedModules} />

					<Divider />

					{/* Voice Calling Configuration */}
					<Collapse>
						<Panel
							header={
								<Space>
									<PhoneOutlined />
									<Title level={5} style={{ margin: 0 }}>
										Voice Calling Configuration
									</Title>
								</Space>
							}
							key='voice-calling'
						>
							<Card>
								<Row gutter={16}>
									<Col span={24}>
										<Item
											label='Enable Voice Calling'
											name={['voiceCalling', 'enabled']}
											valuePropName='checked'
										>
											<Switch />
										</Item>
									</Col>
								</Row>

								<Item
									noStyle
									shouldUpdate={(prevValues, currentValues) =>
										prevValues.voiceCalling?.enabled !==
										currentValues.voiceCalling?.enabled
									}
								>
									{({ getFieldValue }) => {
										const voiceEnabled = getFieldValue(['voiceCalling', 'enabled'])

										if (!voiceEnabled) return null

										return (
											<>
												<Title level={5}>One-time Setup Costs (Reference Only)</Title>
												<Row gutter={16}>
													<Col span={12}>
														<Item
															label='Virtual Number Setup Fee'
															name={['voiceCalling', 'pricing', 'virtualNumberSetupFee']}
															rules={[{ required: true, message: 'Setup fee is required' }]}
															initialValue={3000}
														>
															<InputNumber min={0} style={{ width: '100%' }} />
														</Item>
													</Col>
												</Row>

												<Title level={5}>Monthly Rental Costs (Reference Only)</Title>
												<Row gutter={16}>
													<Col span={8}>
														<Item
															label='Virtual Number Monthly Rental'
															name={['voiceCalling', 'pricing', 'virtualNumberMonthlyRental']}
															rules={[
																{ required: true, message: 'Monthly rental is required' },
															]}
															initialValue={200}
														>
															<InputNumber min={0} style={{ width: '100%' }} />
														</Item>
													</Col>
													<Col span={8}>
														<Item
															label='Per Channel Monthly Rental'
															name={['voiceCalling', 'pricing', 'perChannelMonthlyRental']}
															rules={[
																{ required: true, message: 'Per channel rental is required' },
															]}
															initialValue={650}
														>
															<InputNumber min={0} style={{ width: '100%' }} />
														</Item>
													</Col>
													<Col span={8}>
														<Item
															label='Number of Channels'
															name={['voiceCalling', 'pricing', 'numberOfChannels']}
															rules={[
																{ required: true, message: 'Number of channels is required' },
															]}
															initialValue={1}
														>
															<InputNumber min={1} max={10} style={{ width: '100%' }} />
														</Item>
													</Col>
												</Row>

												<Title level={5}>Per-Call Usage (Active Billing)</Title>
												<Row gutter={16}>
													<Col span={12}>
														<Item
															label='Inbound Per Minute Rate (incoming leg)'
															name={['voiceCalling', 'pricing', 'inboundPerMinuteRate']}
															rules={[{ required: true, message: 'Inbound rate is required' }]}
															initialValue={0.2}
															tooltip='Rate for incoming call leg (customer → system). Used for AI/IVR calls'
														>
															<InputNumber min={0} step={0.01} style={{ width: '100%' }} />
														</Item>
													</Col>
													<Col span={12}>
														<Item
															label='Outbound Per Minute Rate (outgoing leg)'
															name={['voiceCalling', 'pricing', 'outboundPerMinuteRate']}
															rules={[
																{ required: true, message: 'Outbound rate is required' },
															]}
															initialValue={0.2}
															tooltip='Rate for outgoing call leg (system → agent). Used for bridging to agents'
														>
															<InputNumber min={0} step={0.01} style={{ width: '100%' }} />
														</Item>
													</Col>
												</Row>

												<Divider />

												<Title level={5}>EnableX Voice Configuration</Title>
												<Row gutter={16}>
													<Col span={24}>
														<Item
															label='EnableX Account ID'
															name={['enablexVoice', 'appId']}
															rules={[
																{
																	required: false,
																	message: 'EnableX Account ID is required if configuring voice',
																},
															]}
															tooltip='EnableX application ID from EnableX portal'
														>
															<Input placeholder='Enter EnableX Account ID' />
														</Item>
													</Col>
												</Row>
												<Row gutter={16}>
													<Col span={24}>
														<Item
															label='EnableX API Key'
															name={['enablexVoice', 'appKey']}
															rules={[
																{
																	required: false,
																	message: 'EnableX API Key is required if configuring voice',
																},
															]}
															tooltip='EnableX API key from EnableX portal'
														>
															<Input.Password placeholder='Enter EnableX API Key' />
														</Item>
													</Col>
												</Row>
												<Row gutter={16}>
													<Col span={24}>
														<Item
															label='Primary Phone Number'
															name={['enablexVoice', 'fromNumber']}
															rules={[
																{
																	required: false,
																	message:
																		'Primary phone number is required if configuring voice',
																},
																{
																	pattern: /^\+?[1-9]\d{1,14}$/,
																	message:
																		'Please enter a valid phone number with country code (e.g., +1234567890)',
																},
															]}
															tooltip='EnableX virtual number (include country code, e.g., +1234567890)'
														>
															<Input placeholder='+1234567890' />
														</Item>
													</Col>
												</Row>
											</>
										)
									}}
								</Item>
							</Card>
						</Panel>

						{/* AI Services Configuration */}
						<Panel
							header={
								<Space>
									<RobotOutlined />
									<Title level={5} style={{ margin: 0 }}>
										AI Services Configuration
									</Title>
								</Space>
							}
							key='ai-services'
						>
							<Card>
								<Row gutter={16}>
									<Col span={24}>
										<Item
											label='Enable AI Services'
											name={['aiServices', 'enabled']}
											valuePropName='checked'
											preserve
										>
											<Switch />
										</Item>
									</Col>
								</Row>

								<Item
									noStyle
									shouldUpdate={(prevValues, currentValues) =>
										prevValues.aiServices?.enabled !== currentValues.aiServices?.enabled
									}
								>
									{({ getFieldValue }) => {
										const aiEnabled = getFieldValue(['aiServices', 'enabled'])

										if (!aiEnabled) return null

										return (
											<Row gutter={16}>
												<Col span={12}>
													<Item
														label='Currency'
														name={['aiServices', 'pricing', 'currency']}
														rules={[{ required: true, message: 'Currency is required' }]}
														initialValue='INR'
														preserve
													>
														<Select>
															<Select.Option value='INR'>INR</Select.Option>
															<Select.Option value='USD'>USD</Select.Option>
														</Select>
													</Item>
												</Col>
												<Col span={12}>
													<Item
														label='Per Token Rate'
														name={['aiServices', 'pricing', 'perTokenRate']}
														rules={[
															{ required: true, message: 'Per token rate is required' },
														]}
														initialValue={0.002}
														tooltip='Rate charged per AI token (deducted from wallet)'
														preserve
													>
														<InputNumber min={0} step={0.0001} style={{ width: '100%' }} />
													</Item>
												</Col>
												<Col span={12}>
													<Item
														label='Per Transcription Minute Rate'
														name={['aiServices', 'pricing', 'perTranscriptionMinuteRate']}
														initialValue={0}
														tooltip='Per minute of transcribed audio (rounded up). Calls, CRM note audio, and voice notes. Set 0 for BYOK / no charge.'
														preserve
													>
														<InputNumber min={0} step={0.01} style={{ width: '100%' }} />
													</Item>
												</Col>
												<Col span={12}>
													<Item
														label='Per Document Intelligence Unit Rate'
														name={[
															'aiServices',
															'pricing',
															'perDocumentIntelligenceUnitRate',
														]}
														initialValue={0}
														tooltip='Per PDF page or image processed by document intelligence. Set 0 for BYOK / no charge.'
														preserve
													>
														<InputNumber min={0} step={0.01} style={{ width: '100%' }} />
													</Item>
												</Col>
											</Row>
										)
									}}
								</Item>

								<Divider />

								<Title level={5}>AI Platform Credentials</Title>
								<Typography.Paragraph type='secondary' style={{ marginBottom: 16 }}>
									Shared credentials are billed to the client wallet. Bring Your Own Key
									(BYOK) uses the client&apos;s OpenAI key with zero token billing.
								</Typography.Paragraph>
								<Row gutter={16}>
									<Col span={24}>
										<Item
											label='Credential mode'
											name={['aiPlatformConfig', 'credentialMode']}
											initialValue='shared'
											preserve
										>
											<Radio.Group>
												<Radio value='shared'>Shared (DashCX)</Radio>
												<Radio value='byok'>Key (BYOK)</Radio>
											</Radio.Group>
										</Item>
									</Col>
								</Row>
								<Item
									noStyle
									shouldUpdate={(prev, cur) =>
										prev.aiPlatformConfig?.credentialMode !==
										cur.aiPlatformConfig?.credentialMode
									}
								>
									{({ getFieldValue }) => {
										const mode = getFieldValue(['aiPlatformConfig', 'credentialMode'])
										if (mode !== 'byok') return null
										return (
											<Row gutter={16}>
												<Col span={24}>
													<Item
														label='OpenAI API key'
														name={['aiPlatformConfig', 'openai', 'apiKey']}
														tooltip='Leave blank to keep the existing key'
													>
														<Input.Password
															placeholder={
																singleClient.aiPlatformConfig?.openai?.apiKey
																	? '•••••••• (unchanged if empty)'
																	: 'sk-...'
															}
														/>
													</Item>
												</Col>
											</Row>
										)
									}}
								</Item>
								<Row gutter={16}>
									<Col span={24}>
										<Item
											label='Default OpenAI model'
											name={['aiPlatformConfig', 'openai', 'defaultModel']}
											initialValue='gpt-4o-mini'
											tooltip='Used by chatbot, summarizer, and other AI modules'
											preserve
										>
											<Select>
												<Select.Option value='gpt-4o-mini'>gpt-4o-mini</Select.Option>
												<Select.Option value='gpt-4o'>gpt-4o</Select.Option>
												<Select.Option value='gpt-4-turbo'>gpt-4-turbo</Select.Option>
												<Select.Option value='gpt-3.5-turbo'>gpt-3.5-turbo</Select.Option>
											</Select>
										</Item>
									</Col>
								</Row>
							</Card>
						</Panel>
					</Collapse>
				</TabPane>
			</Tabs>

			<Divider />

			<Item>
				<Button type='primary' htmlType='submit' block loading={loading}>
					Update Client
				</Button>
			</Item>
		</Form>
	)
}

export default EditClientForm
